import {
  SheetNode,
  SheetSkillsList,
  SkillsListDifference,
  SkillsListStandard,
  CascadeData,
  CascadeNodeType,
  ImportedSheetData,
} from './types'
import { CASCADE_NODE_TYPES } from './cascade_utils'

/**
 * Calculates differences for existing skills lists between tree and sheet nodes.
 *
 * Compares each skills list in the sheet node with its corresponding skills list
 * in the tree node (matched by nodeid). For each matching pair, checks if:
 * - The title has changed (ignored for question types)
 * - The skills have changed (by comparing sets of skill IDs)
 *
 * Only includes skills lists that exist in both tree and sheet nodes.
 * Skills lists that exist in sheet but not in tree are skipped.
 *
 * @param treeNode - The node from the tree structure
 * @param sheetNode - The node from the imported sheet data
 * @returns An array of SkillsListDifference items for skills lists with detected changes
 */
function calcExistingSkillsDifferences(
  treeNode: SheetNode,
  sheetNode: SheetNode,
): SkillsListDifference[] {
  const differences: SkillsListDifference[] = []

  sheetNode.skillsLists.forEach((sheetSkillsList) => {
    const treeSkillsList = treeNode.skillsLists.find(
      (list) => list.nodeid === sheetSkillsList.nodeid,
    )

    // validate treeSkillsList
    if (!treeSkillsList) {
      return
    }

    // Create dictionaries for tree and sheet standards
    const treeSkillsDict: Record<number, SkillsListStandard> = {}
    const treeSkillsListArray: SkillsListStandard[] = []
    treeSkillsList.skills.forEach((skill) => {
      const standard: SkillsListStandard = {
        standard_id: skill.skill_id,
        standard_name: skill.name,
      }
      treeSkillsDict[skill.skill_id] = standard
      treeSkillsListArray.push(standard)
    })

    const sheetSkillsDict: Record<number, SkillsListStandard> = {}
    sheetSkillsList.skills.forEach((skill) => {
      sheetSkillsDict[skill.skill_id] = {
        standard_id: skill.skill_id,
        standard_name: skill.name,
      }
    })

    const titleChanged =
      sheetNode.type !== 'question' &&
      treeSkillsList.title !== sheetSkillsList.title

    const treeSkillIds = new Set(Object.keys(treeSkillsDict).map(Number))
    const sheetSkillIds = new Set(Object.keys(sheetSkillsDict).map(Number))

    // Check if sets are equal: same size and all elements match
    const setsEqual =
      treeSkillIds.size === sheetSkillIds.size &&
      [...treeSkillIds].every((id) => sheetSkillIds.has(id))
    let skillsChanged = !setsEqual
    let deduplicated = false

    // Check for duplicates: Compare treeSkillsList array length with sheetSkillIds set size.
    // If sets are equal (same unique skill IDs) but treeSkillsList.skills.length differs from
    // sheetSkillIds.size, it indicates duplicates in the tree that need deduplication.
    // This happens when the tree has duplicate skill entries but the sheet has unique entries.
    if (setsEqual && treeSkillsList.skills.length !== sheetSkillIds.size) {
      skillsChanged = true
      deduplicated = true
    }

    // If neither title nor skills changed, skip this skills list
    if (!titleChanged && !skillsChanged) {
      return
    }

    differences.push({
      skills_list_node_id: sheetSkillsList.nodeid,
      skills_list_title: sheetSkillsList.title,
      skills_list_parent_node_id: sheetNode.node_id,
      skills_list_parent_node_type: sheetNode.type,
      skills_list_parent_node_title: sheetNode.title,
      tree_skills_list: treeSkillsListArray,
      tree_skills: treeSkillsDict,
      sheet_skills: sheetSkillsDict,
      new_skills_list: false,
      title_change: titleChanged,
      skills_changed: skillsChanged,
      deduplicated: deduplicated,
    })
  })

  return differences
}

/**
 * Calculates differences for new skills lists from the sheet node.
 *
 * Processes all skills lists in the sheet node's newSkillsLists array.
 * These are skills lists that don't exist in the tree yet and need to be created.
 *
 * For new skills lists:
 * - tree_skills will be empty (no existing skills in tree)
 * - title_change is always true (new list)
 * - skills_changed is always true (new list)
 * - skills_list_node_id is empty (no existing node ID)
 *
 * @param sheetNode - The node from the imported sheet data containing new skills lists
 * @returns An array of SkillsListDifference items, one for each new skills list
 */
function calcNewSkillsDifferences(
  sheetNode: SheetNode,
): SkillsListDifference[] {
  const differences: SkillsListDifference[] = []

  sheetNode.newSkillsLists.forEach((newSkillsList) => {
    // Create dictionary for sheet standards
    const sheetSkillsDict: Record<number, SkillsListStandard> = {}
    newSkillsList.skills.forEach((skill) => {
      sheetSkillsDict[skill.skill_id] = {
        standard_id: skill.skill_id,
        standard_name: skill.name,
      }
    })

    // For new skills lists, tree_skills will be empty
    const treeSkillsDict: Record<number, SkillsListStandard> = {}

    differences.push({
      skills_list_node_id: '', // Empty for new skills lists
      skills_list_title: newSkillsList.title,
      skills_list_parent_node_id: sheetNode.node_id,
      skills_list_parent_node_type: sheetNode.type,
      skills_list_parent_node_title: sheetNode.title,
      tree_skills_list: [], // Empty array for new skills lists
      tree_skills: treeSkillsDict,
      sheet_skills: sheetSkillsDict,
      new_skills_list: true, // This is a new skills list
      title_change: true, // New skills list always has title change
      skills_changed: true, // New skills list always has skills changed
      deduplicated: false,
    })
  })

  return differences
}

/**
 * Compares skills lists between sheet and tree nodes and returns differences and summary.
 * Matches nodes by nodeid and skills lists by skills list nodeid.
 * Returns a list with skills_list id, tree skills, and sheet skills, along with a summary.
 */
export function findSkillsListDifferences(
  importedSheetData: ImportedSheetData | null,
  treeNodes: SheetNode[],
  cascadeData: CascadeData | null,
): SkillsListDifference[] {
  // Check for "no data" conditions
  if (!importedSheetData || treeNodes.length === 0) {
    return []
  }

  const treeMap = new Map<string, SheetNode>()
  treeNodes.forEach((node) => {
    treeMap.set(node.node_id, node)
  })

  // Build sheet node map: use cascade-modified nodes when cascade is active, otherwise original nodes
  const sheetNodeMap = new Map<string, SheetNode>()
  const modifiedNodes = cascadeData?.cascadeResults?.modifiedNodes

  // When cascade is active, only process nodes that were modified by cascade
  // When cascade is not active, process all nodes
  const nodesToProcess =
    cascadeData && modifiedNodes
      ? Object.keys(modifiedNodes) // Only modified nodes when cascade is active
      : importedSheetData.nodeList.map((node) => node.node_id) // All nodes when cascade is not active

  nodesToProcess.forEach((nodeId) => {
    const originalNode = importedSheetData.nodeList.find(
      (n) => n.node_id === nodeId,
    )
    if (!originalNode) {
      return
    }

    // Use modified node if cascade is active and node is in modifiedNodes, otherwise use original
    if (cascadeData && modifiedNodes && modifiedNodes[nodeId]) {
      sheetNodeMap.set(nodeId, modifiedNodes[nodeId])
    } else {
      sheetNodeMap.set(nodeId, originalNode)
    }
  })

  const differences: SkillsListDifference[] = []

  // For each sheetNode, find corresponding treeNode and compare
  sheetNodeMap.forEach((sheetNode) => {
    const treeNode = treeMap.get(sheetNode.node_id)
    if (!treeNode) {
      // Node exists in sheet but not in tree - skip
      return
    }

    // Process existing skills lists
    const existingDiffs = calcExistingSkillsDifferences(treeNode, sheetNode)
    differences.push(...existingDiffs)

    // Process new skills lists
    const newDiffs = calcNewSkillsDifferences(sheetNode)
    differences.push(...newDiffs)
  })

  return differences
}

/**
 * Calculate summary rows for sync tree display
 */
export function calculateSyncSummaryRows(
  syncDifferences: SkillsListDifference[],
  treeId: string,
  versionId: string,
  documentTypes: string[] = [],
): Array<{ cells: (string | number)[] }> {
  // Count nodes changed by type
  const nodesChangedByType = new Map<string, Set<string>>()
  let totalSkillsListsChanged = 0
  let totalSkillsListsAdded = 0
  let totalDuplicatesFixed = 0

  syncDifferences.forEach((diff) => {
    // Count nodes changed by type (only if at least one of the three flags is set)
    if (diff.new_skills_list || diff.skills_changed || diff.title_change) {
      const type = diff.skills_list_parent_node_type
      const nodeId = diff.skills_list_parent_node_id
      if (type && nodeId) {
        if (!nodesChangedByType.has(type)) {
          nodesChangedByType.set(type, new Set())
        }
        nodesChangedByType.get(type)!.add(nodeId)
      }
    }

    // Count skills lists added
    if (diff.new_skills_list) {
      totalSkillsListsAdded++
    }

    // Count skills lists changed (only if skills_changed flag is set)
    if (diff.skills_changed) {
      totalSkillsListsChanged++
    }

    // Count duplicates fixed
    if (diff.deduplicated) {
      totalDuplicatesFixed++
    }
  })

  const rows: Array<{ cells: (string | number)[] }> = []

  // Add nodes changed by type, using documentTypes order if available
  // Fallback to CASCADE_NODE_TYPES if documentTypes is empty
  const orderedTypes =
    documentTypes.length > 0
      ? documentTypes.filter((type) =>
          CASCADE_NODE_TYPES.includes(type as CascadeNodeType),
        )
      : CASCADE_NODE_TYPES

  orderedTypes.forEach((type) => {
    const count = nodesChangedByType.get(type)?.size || 0
    if (count > 0) {
      rows.push({ cells: [`${type} nodes changed`, count] })
    }
  })

  // Add summary totals
  rows.push({ cells: ['total skills lists changed', totalSkillsListsChanged] })
  rows.push({ cells: ['total skills lists added', totalSkillsListsAdded] })
  rows.push({
    cells: ['total nodes with duplicates fixed', totalDuplicatesFixed],
  })

  return rows
}
